<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-tabs v-model="selectedTab" no-fade lazy align="center">
          <b-tab v-for="(locale, index) in locales" :key="locale.code" :active="index === 0" :title="locale.label"
            class="mt-3 border">
            <component :is="tabComponent" :locale="locale.code" v-bind="tabProps" @content-updated="onContentUpdated" />
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>

    <!-- BUTTONS -->
    <b-row class="pt-3 mb-1 d-flex justify-content-between justify-content-sm-end">
      <b-col v-if="markAsSentButtonVisible" cols="12" sm="auto" class="mb-2 mb-sm-0">
        <b-button block @click="markEmailAsSent" variant="secondary">
          Marca com a enviat
        </b-button>
      </b-col>
      <b-col cols="12" sm="auto" class="send-button">
        <b-button block :disabled="!contentClientEmail" variant="primary" @click="sendEmail">
          Envia
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { BContainer, BRow, BCol, BTabs, BTab, BButton } from "bootstrap-vue";
import { notifyError } from "@/utils/methods";
import axios from "@/axios";

// Lazy load components
const componentImports = {
  'prebooking-confirmation': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormPrebookingConfirmation.vue"),
  'booking-confirmation': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormBookingConfirmation.vue"),
  'booking-modification': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormBookingModification.vue"),
  'booking-cancellation': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormBookingCancellation.vue"),
  'checkin-instructions': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormCheckinInstructions.vue"),
  'manual': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormManualNotification.vue"),
  'initial-payment-request': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormInitialPaymentRequest.vue"),
  'final-payment-request': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormFinalPaymentRequest.vue"),
  'last-final-payment-request': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormFinalPaymentRequest.vue"),
  'online-checkin-request': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormOnlineCheckinRequest.vue"),
  'review-request': () => import("@/views/bookings/booking/components/notifications/ClientEmailNotificationFormReviewRequest.vue"),
};

// Notification configuration
const NOTIFICATION_CONFIG = {
  "prebooking-confirmation": {
    tabProps: null,
    urlPath: "prebooking-confirmation",
    sendLogType: "CLIENT_PREBOOKING_CONFIRMATION_SENT",
    markedAsSentLogType: "CLIENT_PREBOOKING_CONFIRMATION_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "booking-confirmation": {
    tabProps: null,
    urlPath: "booking-confirmation",
    sendLogType: "CLIENT_BOOKING_CONFIRMATION_SENT",
    markedAsSentLogType: "CLIENT_BOOKING_CONFIRMATION_MARKED_AS_SENT",
    updateStatsProps: { clientBookingConfirmationSent: true, clientBookingConfirmationSentDate: new Date() },
    showMarkAsSent: true,
  },
  "booking-modification": {
    tabProps: null,
    urlPath: "booking-modification",
    sendLogType: "CLIENT_BOOKING_MODIFICATION_SENT",
    markedAsSentLogType: "CLIENT_BOOKING_MODIFICATION_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "booking-cancellation": {
    tabProps: null,
    urlPath: "booking-cancellation",
    sendLogType: "CLIENT_BOOKING_CANCELLATION_SENT",
    markedAsSentLogType: "CLIENT_BOOKING_CANCELLATION_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "checkin-instructions": {
    tabProps: null,
    urlPath: "checkin-instructions",
    sendLogType: "CLIENT_CHECKIN_INSTRUCTIONS_SENT",
    markedAsSentLogType: "CLIENT_CHECKIN_INSTRUCTIONS_MARKED_AS_SENT",
    updateStatsProps: { clientArrivalInstructionsSent: true, clientArrivalInstructionsSentDate: new Date() },
    showMarkAsSent: true,
  },
  "manual": {
    tabProps: null,
    urlPath: "manual-notification",
    sendLogType: "CLIENT_MANUAL_NOTIFICATION_SENT",
    markedAsSentLogType: "CLIENT_MANUAL_NOTIFICATION_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "initial-payment-request": {
    tabProps: null,
    urlPath: "initial-payment-request",
    sendLogType: "CLIENT_INITIAL_PAYMENT_REQUEST_SENT",
    markedAsSentLogType: "CLIENT_INITIAL_PAYMENT_REQUEST_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "final-payment-request": {
    tabProps: { last: false },
    urlPath: "final-payment-request",
    sendLogType: "CLIENT_FINAL_PAYMENT_REQUEST_SENT",
    markedAsSentLogType: "CLIENT_FINAL_PAYMENT_REQUEST_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "last-final-payment-request": {
    tabProps: { last: true },
    urlPath: "final-payment-request",
    sendLogType: "CLIENT_LAST_FINAL_PAYMENT_REQUEST_SENT",
    markedAsSentLogType: "CLIENT_LAST_FINAL_PAYMENT_REQUEST_MARKED_AS_SENT",
    updateStatsProps: null,
  },
  "online-checkin-request": {
    tabProps: null,
    urlPath: "online-checkin-request",
    sendLogType: "CLIENT_ONLINE_CHECKIN_REQUEST_SENT",
    markedAsSentLogType: "CLIENT_ONLINE_CHECKIN_REQUEST_MARKED_AS_SENT",
    updateStatsProps: { clientInformationRequestSent: true, clientInformationRequestSentDate: new Date() },
  },
  "review-request": {
    tabProps: null,
    urlPath: null,
    sendLogType: null,
    markedAsSentLogType: null,
    updateStatsProps: null,
  },
};

// Language locales configuration
const LOCALES = [
  { code: 'en-GB', label: 'English', shortCode: 'en' },
  { code: 'de-DE', label: 'Deutsch', shortCode: 'de' },
  { code: 'es-ES', label: 'Castellano', shortCode: 'es' },
  { code: 'ca-ES', label: 'Català', shortCode: 'ca' },
];

export default {
  props: {
    notificationType: {
      type: String,
      required: true,
      validator(value) {
        return Object.keys(NOTIFICATION_CONFIG).includes(value);
      },
    },
  },
  components: {
    BContainer,
    BRow,
    BCol,
    BTabs,
    BTab,
    BButton,
  },
  data() {
    return {
      selectedTab: 0,
      content: null,
      locales: LOCALES,
    };
  },
  computed: {
    booking() {
      return this.$store.getters["booking/booking"];
    },
    stats() {
      return this.$store.getters["booking/stats"];
    },
    client() {
      return this.$store.getters["booking/client"];
    },
    contentClientEmail() {
      return this.content?.clientEmail || null;
    },
    notificationConfig() {
      return NOTIFICATION_CONFIG[this.notificationType] || {};
    },
    tabComponent() {
      return componentImports[this.notificationType] || null;
    },
    tabProps() {
      return this.notificationConfig.tabProps;
    },
    postUrl() {
      const urlPath = this.notificationConfig.urlPath;
      return urlPath ? `/mail/client/${urlPath}/${this.content.locale}` : null;
    },
    sendLogType() {
      return this.notificationConfig.sendLogType;
    },
    markedAsSentLogType() {
      return this.notificationConfig.markedAsSentLogType;
    },
    updateStatsProps() {
      return this.notificationConfig.updateStatsProps;
    },
    markAsSentButtonVisible() {
      return this.notificationConfig.showMarkAsSent || false;
    },
  },
  mounted() {
    setTimeout(() => {
      this.initTab();
    }, 50);
  },
  methods: {
    initTab() {
      if (!this.client) return;

      const language = this.client?.language || "en";
      const tabIndex = LOCALES.findIndex(locale => locale.shortCode === language);
      this.selectedTab = tabIndex !== -1 ? tabIndex : 0;
    },
    onContentUpdated(content) {
      this.content = content;
    },
    async sendEmail() {
      if (!this.contentClientEmail) {
        notifyError("Error", "El client d'aquesta reserva no disposa de correu electrònic");
        this.$emit("send-error");
        return;
      }

      if (!this.postUrl) {
        notifyError("Error", "No hi ha cap url definida per aquest tipus de notificació");
        this.$emit("send-error");
        return;
      }

      this.$store.dispatch("app/setLoading", true);

      try {
        await axios.post(this.postUrl, this.content);

      // Save the log entry, if needed
        if (this.sendLogType) {
          await this.$store.dispatch("booking/addLog", {
            booking: this.booking["@id"],
            type: this.sendLogType,
            data: this.content,
          });
        }

        this.$emit("sent");
      } catch (error) {
        this.$emit("send-error");
      } finally {
        this.$store.dispatch("app/setLoading", false);
      }
    },
    async markEmailAsSent() {
      this.$store.dispatch("app/setLoading", true);

      try {
        // Update booking stats
        if (this.updateStatsProps !== null) {
          if (!this.stats) {
            throw new Error("No s'han pogut recuperar les estadístiques de la reserva per actualitzar-les");
          }

          await this.$store.dispatch("booking/updateStats", {
            uuid: this.stats.uuid,
            ...this.updateStatsProps,
          });
        }

        // Save the log entry, if needed
        if (this.markedAsSentLogType) {
          await this.$store.dispatch("booking/addLog", {
            booking: this.booking["@id"],
            type: this.markedAsSentLogType,
          });
        } else {
          throw new Error("Unsupported log type");
        }

        this.$emit("marked-as-sent");
      } catch (error) {
        notifyError("Error", error.message);
        this.$emit("mark-as-sent-error");
      } finally {
        this.$store.dispatch("app/setLoading", false);
      }
    },
  },
};
</script>
